'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var operators = require('rxjs/operators');
var rxjs = require('rxjs');

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var ofTypes = function (types) { return function (dispatcher$) {
    return dispatcher$.pipe(operators.filter(function (_a) {
        var type = _a.type;
        return types.includes(type);
    }));
}; };

var addEffects = function (actionCreator, scopedEffects) {
    return function (payload) { return (__assign(__assign({}, actionCreator(payload)), { scopedEffects: scopedEffects(payload) })); };
};

var createSlice = function (config) {
    var initialState = config.initialState, reducers = config.reducers;
    var reducer = Object.entries(reducers).reduce(function (acc, _a) {
        var key = _a[0], _case = _a[1];
        var _reducer = typeof _case === 'function' ? _case : _case.reducer;
        var newFunc = function (state, action) {
            if (action && action.type === key) {
                return _reducer(state, action);
            }
            return acc(state, action);
        };
        return newFunc;
    }, function (state) {
        if (state === void 0) { state = initialState; }
        return state;
    });
    var actions = Object.entries(reducers).reduce(function (acc, _a) {
        var key = _a[0], _case = _a[1];
        acc[key] = function (payload) { return ({
            type: key,
            payload: payload
        }); };
        if (typeof _case !== 'function' && _case.effects) {
            var effects = typeof _case.effects === 'function'
                ? _case.effects
                : (function () { return ({ effects: _case.effects }); });
            acc[key] = addEffects(acc[key], effects);
        }
        return acc;
    }, {});
    return {
        reducer: reducer,
        actions: actions
    };
};

var jsonDiff = function (obj, newObj, options, _stack) {
    if (options === void 0) { options = { cyclesFix: true }; }
    if (_stack === void 0) { _stack = []; }
    var diffs = [];
    var isObjArray = Array.isArray(obj);
    var _loop_1 = function (key) {
        var objKey = obj[key];
        var path = isObjArray ? +key : key;
        if (!(key in newObj)) {
            diffs.push({
                type: 'REMOVE',
                path: [path],
                oldValue: obj[key]
            });
            return "continue";
        }
        var newObjKey = newObj[key];
        var areObjects = typeof objKey === 'object' && typeof newObjKey === 'object';
        if (objKey && newObjKey && areObjects && (!options.cyclesFix || !_stack.includes(objKey))) {
            var nestedDiffs = jsonDiff(objKey, newObjKey, options, options.cyclesFix ? _stack.concat([objKey]) : []);
            // eslint-disable-next-line prefer-spread
            diffs.push.apply(diffs, nestedDiffs.map(function (difference) {
                difference.path.unshift(path);
                return difference;
            }));
        }
        else if (objKey !== newObjKey &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            !(areObjects && (isNaN(objKey) ? objKey + '' === newObjKey + '' : +objKey === +newObjKey))) {
            diffs.push({
                path: [path],
                type: 'CHANGE',
                value: newObjKey,
                oldValue: objKey
            });
        }
    };
    for (var key in obj) {
        _loop_1(key);
    }
    var isNewObjArray = Array.isArray(newObj);
    for (var key in newObj) {
        if (!(key in obj)) {
            diffs.push({
                type: 'CREATE',
                path: [isNewObjArray ? +key : key],
                value: newObj[key]
            });
        }
    }
    return diffs;
};

var getScopedEffectSignature = function (actionType, key) {
    return "type: ".concat(actionType, ", scoped: true").concat(key ? ",key:".concat(key) : '');
};
var HubFactory = function (_a) {
    var _b = _a === void 0 ? {} : _a, effects = _b.effects, _c = _b.sources, sources = _c === void 0 ? [] : _c;
    var dispatcher$ = new rxjs.ReplaySubject(1);
    var inputStream$ = rxjs.merge.apply(void 0, __spreadArray([dispatcher$], sources.map(function (source) { return source.pipe(operators.shareReplay(1)); }), false));
    var genericEffects = (effects === null || effects === void 0 ? void 0 : effects.reduce(function (result, effect) {
        return result.concat(inputStream$.pipe(effect));
    }, [])) || [];
    // Should we keep this in the stream with a scan operator instead?
    var scopedEffectsDict = {};
    var mergedScopedEffects = inputStream$.pipe(operators.filter(function (_a) {
        var type = _a.type, scopedEffects = _a.scopedEffects;
        var hasEffects = Boolean(scopedEffects && scopedEffects.effects.length);
        return (hasEffects &&
            scopedEffectsDict[getScopedEffectSignature(type, scopedEffects.key)] === undefined);
    }), operators.tap(function (_a) {
        var type = _a.type, _b = _a.scopedEffects, key = _b.key, effects = _b.effects;
        scopedEffectsDict[getScopedEffectSignature(type, key)] = effects;
    }), operators.map(function (_a) {
        var type = _a.type, _b = _a.scopedEffects, key = _b.key, effects = _b.effects;
        var signature = getScopedEffectSignature(type, key);
        var pipedEffects = effects.reduce(function (acc, effect) {
            return acc.concat(inputStream$.pipe(operators.filter(function (initialAction) {
                var _a;
                return getScopedEffectSignature(initialAction.type, (_a = initialAction.scopedEffects) === null || _a === void 0 ? void 0 : _a.key) ===
                    signature;
            }), effect));
        }, []);
        return rxjs.merge.apply(void 0, pipedEffects);
    }), operators.mergeAll());
    var messages$ = rxjs.merge.apply(void 0, __spreadArray([inputStream$, mergedScopedEffects], genericEffects, false)).pipe(operators.share());
    var store = function (_a) {
        var reducer = _a.reducer, name = _a.name, debug = _a.debug, initialState = _a.initialState, _b = _a.storeValue, storeValue = _b === void 0 ? false : _b;
        var debugName = "[RX NAME] ".concat(name || 'undefined', "\n");
        var seedState = initialState !== undefined ? initialState : reducer();
        var state$ = messages$.pipe(operators.tap(function (action) {
            debug && console.log(debugName, '[ACTION]', action, '\n');
        }), operators.scan(reducer, seedState), operators.startWith(null, seedState), operators.pairwise(), operators.tap(function (_a) {
            var prevState = _a[0], newState = _a[1];
            if (debug) {
                if (prevState &&
                    typeof prevState === 'object' &&
                    newState &&
                    typeof newState === 'object') {
                    try {
                        var reduceDiff = function (diff) {
                            return diff.reduce(function (acc, change) {
                                var _a;
                                return (__assign(__assign({}, acc), (_a = {}, _a[change.path.join('|')] = change, _a)));
                            }, {});
                        };
                        var difference = reduceDiff(jsonDiff(prevState, newState));
                        console.log(debugName, '[DIFF]', Object.keys(difference).length ? difference : null, '\n', '[STATE]', newState, '\n');
                    }
                    catch (e) {
                        console.log('[ERROR READING DIFF]', e, '\n', '[STATE]', newState);
                    }
                }
                else {
                    var hasDiff = prevState !== newState;
                    console.log(debugName, '[DIFF]', hasDiff
                        ? {
                            oldValue: prevState,
                            newValue: newState
                        }
                        : null, '\n', '[STATE]', newState);
                }
            }
        }), operators.map(function (pair) { return pair[1]; }));
        if (storeValue) {
            var replaySubject_1 = new rxjs.ReplaySubject(1);
            state$.subscribe(function (state) { return replaySubject_1.next(state); });
            return replaySubject_1;
        }
        return state$;
    };
    return {
        messages$: messages$,
        store: store,
        dispatch: function () {
            var actions = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                actions[_i] = arguments[_i];
            }
            actions.forEach(function (action) {
                dispatcher$.next(action);
            });
        }
    };
};

var RxBuilder = function (_a) {
    var effects = _a.effects, _b = _a.sources, sources = _b === void 0 ? [] : _b, _c = _a.debug, debug = _c === void 0 ? false : _c, _d = _a.storeValue, storeValue = _d === void 0 ? false : _d, sliceConfig = __rest(_a, ["effects", "sources", "debug", "storeValue"]);
    var _e = createSlice(sliceConfig), reducer = _e.reducer, actions = _e.actions;
    // Check sources and see if need to add effects
    if (!Array.isArray(sources)) {
        sources = Object.entries(sources).map(function (_a) {
            var key = _a[0], obs$ = _a[1];
            return obs$.pipe(operators.map(function (value) { return ({ type: key, payload: value }); }));
        });
    }
    sources = sources.map(function (action$) {
        return action$.pipe(operators.map(function (action) {
            var _case = sliceConfig.reducers[action.type];
            if (_case && typeof _case !== 'function' && _case.effects) {
                var effects_1 = typeof _case.effects === 'function'
                    ? _case.effects
                    : (function () { return ({ effects: _case.effects }); });
                return __assign(__assign({}, action), { scopedEffects: effects_1(action.payload) });
            }
            return action;
        }));
    });
    var hub = HubFactory({ effects: effects, sources: sources });
    var actionsResult = Object.fromEntries(Object.entries(actions).map(function (_a) {
        var key = _a[0], actionCreator = _a[1];
        return [
            key,
            function (payload) {
                hub.dispatch(actionCreator(payload));
            },
        ];
    }));
    return [
        hub.store({ reducer: reducer, debug: debug, storeValue: storeValue, name: sliceConfig.name }),
        actionsResult,
        hub.messages$,
    ];
};

var storeValue = function (reactable) {
    var replaySubject$ = new rxjs.ReplaySubject(1);
    var state$ = reactable[0], actions = reactable[1], actions$ = reactable[2];
    var subscription = state$.subscribe(function (state) { return replaySubject$.next(state); });
    var destroy = function () { return subscription.unsubscribe(); };
    return [replaySubject$, __assign(__assign({}, actions), { destroy: destroy }), actions$];
};

exports.RxBuilder = RxBuilder;
exports.ofTypes = ofTypes;
exports.storeValue = storeValue;
